/**
 * Cost-by-model pie chart using Cortex design tokens via {@link useChartTokens}.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, LoadingSkeleton } from '@coordio/ui/layout';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

import { dashboardService } from '../../services/dashboardService';
import { useContextStore } from '../../store/contextStore';
import { chartTooltipStyle, useChartTokens } from './useChartTokens';

import './dashboardWidgets.scss';

type CostSlice = { name: string; value: number };

type CostByModelChartProps = {
  data?: CostSlice[];
  loading?: boolean;
  rangeDays?: number;
  topN?: number;
  className?: string;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(value);

const rangeToUtc = (rangeDays: number) => {
  const end = new Date();
  const start = new Date(Date.now() - rangeDays * 24 * 60 * 60 * 1000);
  const toYmd = (d: Date) => d.toISOString().slice(0, 10);
  return { startDateUtc: toYmd(start), endDateUtc: toYmd(end) };
};

/**
 * Pie of spend by model for the selected range.
 */
export const CostByModelChart = ({
  data: dataProp,
  loading: loadingProp,
  rangeDays = 30,
  topN = 6,
  className,
}: CostByModelChartProps) => {
  const tokens = useChartTokens();
  const { currentTenant, currentProject } = useContextStore();
  const hasTenant = !!currentTenant?.id;
  const shouldFetch = dataProp === undefined;

  const { startDateUtc, endDateUtc } = useMemo(() => rangeToUtc(rangeDays), [rangeDays]);

  const { data: fetched, isLoading: fetchLoading } = useQuery({
    queryKey: ['dashboard', 'costBreakdown', currentTenant?.id, currentProject?.id, startDateUtc, endDateUtc],
    queryFn: () => dashboardService.getCostBreakdown(startDateUtc, endDateUtc),
    enabled: shouldFetch && hasTenant,
  });

  const chartData =
    dataProp ??
    (fetched?.byModel ?? [])
      .filter((x) => x.cost > 0)
      .slice(0, topN)
      .map((x) => ({ name: x.name, value: x.cost }));

  const loading = loadingProp ?? (shouldFetch && fetchLoading);

  return (
    <div className={className ? `cost-by-model-chart ${className}` : 'cost-by-model-chart'} data-testid="cost-by-model-chart">
      <div className="cost-by-model-chart__plot">
        {loading ? (
          <LoadingSkeleton rows={4} />
        ) : chartData.length === 0 ? (
          <EmptyState title="No cost data" description="Model spend appears once agents make billable calls." />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {chartData.map((slice, index) => (
                  <Cell key={slice.name} fill={tokens.series[index % tokens.series.length]} stroke="none" />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value) => formatCurrency(Number(value))} contentStyle={chartTooltipStyle(tokens)} />
              <Legend wrapperStyle={{ fontSize: 12, color: tokens.axis }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
